import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Spinner } from '@phosphor-icons/react';
import { OnboardingWelcome } from '../components/onboarding/OnboardingWelcome';
import { OnboardingConditions } from '../components/onboarding/OnboardingConditions';
import { OnboardingMedications } from '../components/onboarding/OnboardingMedications';
import { OnboardingCurrentState } from '../components/onboarding/OnboardingCurrentState';
import { OnboardingHistory } from '../components/onboarding/OnboardingHistory';
import { OnboardingMedicalTeam } from '../components/onboarding/OnboardingMedicalTeam';
import { OnboardingLifeContext } from '../components/onboarding/OnboardingLifeContext';
import { OnboardingGoals } from '../components/onboarding/OnboardingGoals';
import { OnboardingDocuments } from '../components/onboarding/OnboardingDocuments';
import { OnboardingDataAwareness } from '../components/onboarding/OnboardingDataAwareness';
import { OnboardingSummary } from '../components/onboarding/OnboardingSummary';
import { track, ACTIVATION_EVENTS } from '../services/activation';
import { useOnboardingAbandoned } from '../hooks/useFrictionEvents';

/*
  New onboarding flow (11 steps, 0-10):
  0 = Welcome
  1 = Conditions
  2 = Medications
  3 = Current State
  4 = Medical History
  5 = Medical Team (NEW)
  6 = Life Context
  7 = Goals
  8 = Documents (optional)
  9 = Data Awareness
  10 = Review / Summary
*/
const TOTAL_STEPS = 11;

const STEP_LABELS = {
  0: 'Welcome',
  1: 'Conditions',
  2: 'Medications',
  3: 'Current State',
  4: 'Medical History',
  5: 'Care Team (optional)',
  6: 'Life Context',
  7: 'Goals',
  8: 'Documents (optional)',
  9: 'Almost there',
  10: 'Review',
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState({
    conditions: [],
    suspected_conditions: [],
    medications: [],
    current_state: { feeling_description: '', current_issues: [], hardest_right_now: '', overall_signal: '' },
    medical_history: { nothing_significant: false, past_conditions: [], hospitalisations: [], surgeries: [], recurring_issues: [] },
    medical_team: [],
    life_context: { work: '', stress_level: '', sleep_quality: '', support: '', additional_notes: '' },
    goals: { selected: [], custom: '' },
  });
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [completeError, setCompleteError] = useState('');

  // Friction tracking — fire onboarding_abandoned on idle/unmount if not completed.
  useOnboardingAbandoned({ stepIndex: step, completed: !!user?.onboarding_completed, totalSteps: TOTAL_STEPS });

  // Defensive guard: if somehow this page re-mounts with a user who has already
  // completed onboarding, redirect out immediately instead of letting them
  // re-do the flow. Prevents any "completed then back to start" loop.
  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/home', { replace: true });
    }
  }, [user?.onboarding_completed, navigate]);  // Fire onboarding_start once per user when this page mounts for a user
  // who has NOT yet completed onboarding.
  useEffect(() => {
    if (user && !user.onboarding_completed) {
      track(ACTIVATION_EVENTS.ONBOARDING_START);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?._id]);

  // Resume from saved progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const { data: progress } = await axios.get(`${API}/onboarding/progress`);
        if (progress.current_step > 0 && progress.data) {
          const stepData = progress.data;
          const merged = { ...data };
          Object.values(stepData).forEach(sd => {
            if (sd && typeof sd === 'object') {
              Object.entries(sd).forEach(([key, val]) => {
                if (key in merged) merged[key] = val;
              });
            }
          });
          setData(merged);
          setStep(progress.current_step);
        }
      } catch { /* first visit */ }
    };
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveStep = useCallback(async (stepNum) => {
    try {
      await axios.post(`${API}/onboarding/save-step`, { step: stepNum, data });
    } catch { /* non-critical */ }
  }, [data]);

  const goForward = () => {
    if (step < TOTAL_STEPS - 1) {
      setDirection(1);
      const nextStep = step + 1;
      setStep(nextStep);
      saveStep(nextStep);
    }
  };

  const goBack = () => {
    if (step > 0) { setDirection(-1); setStep(step - 1); }
  };

  const goToStep = (targetStep) => {
    setDirection(targetStep > step ? 1 : -1);
    setStep(targetStep);
  };

  const skipOnboarding = async () => {
    try {
      await axios.post(`${API}/onboarding/complete`, {});
    } catch { /* fall through to authoritative check */ }
    try { await refreshUser(); } catch {}
    // Authoritative check so skip doesn't silently loop.
    try {
      const { data: me } = await axios.get(`${API}/auth/me`);
      if (!me?.onboarding_completed) {
        setCompleteError("We couldn't skip setup. Please try again.");
        return;
      }
    } catch {}
    track(ACTIVATION_EVENTS.ONBOARDING_FINISH, { path: 'skip' });
    navigate('/welcome');
  };

  const handleComplete = async () => {
    setSubmitting(true);
    setCompleteError('');
    try {
      // Backend now returns in ~100ms (heavy LLM knowledge gen moved to
      // FastAPI BackgroundTasks which runs AFTER the response is flushed).
      // No race-condition timeout wrapper needed.
      await axios.post(`${API}/onboarding/complete`, data);
    } catch (err) {
      console.warn('Onboarding completion failed:', err?.message);
    }
    try { await refreshUser(); } catch {}
    // Authoritative server check — did onboarding_completed actually flip?
    let serverSaysCompleted = false;
    try {
      const { data: me } = await axios.get(`${API}/auth/me`);
      serverSaysCompleted = !!me?.onboarding_completed;
    } catch {}
    if (serverSaysCompleted) {
      track(ACTIVATION_EVENTS.ONBOARDING_FINISH, { path: 'complete' });
      navigate('/welcome');
    } else {
      setCompleteError("We couldn't finish setting up your profile. Please try again.");
      setSubmitting(false);
    }
  };

  const canContinue = () => {
    if (step === 7) return (data.goals.selected || []).length > 0;
    return true;
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  const progressPct = step === 0 ? 0 : (step / (TOTAL_STEPS - 1)) * 100;

  // Stage 17 — editorial left-panel copy shown on lg+ only.
  // Purpose: fill the huge empty canvas with warm, reassuring
  // explanation so the flow feels premium on desktop without
  // breaking mobile (mobile hides this aside entirely).
  const LEFT_PANEL_COPY = {
    0:  { eyebrow: "Care between appointments", title: "A calm place to start.", body: "Kindra remembers what your doctor says, explains what you don't understand, and keeps everything in one place — so the next visit isn't starting from scratch." },
    1:  { eyebrow: "Conditions", title: "What you're living with.", body: "Add anything you've been diagnosed with or are tracking — physical, mental, chronic or temporary. This helps Kindra read every future report in your context. Skip anything you'd rather add later." },
    2:  { eyebrow: "Medications", title: "Prescriptions, supplements, anything regular.", body: "Doses, vitamins, contraceptives, HRT — whatever you're taking regularly. Kindra will cross-check it against reports and flag interactions worth asking your doctor about. Approximate is fine." },
    3:  { eyebrow: "How you're feeling", title: "Today, not forever.", body: "A quick signal about right now. Kindra uses this as a baseline so future changes are visible. One sentence is enough." },
    4:  { eyebrow: "History", title: "Past operations, hospital stays, recurring issues.", body: "Only the ones that matter to you. The summary version is perfect — Kindra will learn the detail as reports come in." },
    5:  { eyebrow: "Care team (optional)", title: "GP, specialists, physio.", body: "So when you say \"my GP said...\", Kindra knows who you mean. Optional — add names as you go." },
    6:  { eyebrow: "Life context", title: "The stuff that shapes a health question.", body: "Work, sleep, stress, who's around — these change how a result gets interpreted. Kindra doesn't judge, it just listens better with context." },
    7:  { eyebrow: "Goals", title: "What 'useful' looks like for you.", body: "Choose the outcomes that matter. Kindra shapes its answers around these — not one-size-fits-all." },
    8:  { eyebrow: "Reports (optional)", title: "The fastest way to make Kindra useful.", body: "Drop in a blood test, scan, or letter. Kindra reads it, explains it in plain English, and keeps the findings in your record forever. You can also do this later." },
    9:  { eyebrow: "Privacy", title: "Your data is yours.", body: "Encrypted at rest. Never used to train models. Deletable at any time. Kindra is a health companion — not a clinic, not a marketplace." },
    10: { eyebrow: "Review", title: "One last look.", body: "Everything you've added so far. Edit anything that's wrong, then confirm to start using Kindra." },
  };
  const leftCopy = LEFT_PANEL_COPY[step] || LEFT_PANEL_COPY[0];

  return (
    <div className="min-h-[100dvh] bg-[#FAFAFA] flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-slate-100 px-4 py-3 safe-top">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button onClick={goBack} className="text-slate-400 hover:text-slate-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="onboarding-back">
                <ArrowLeft size={20} />
              </button>
            )}
            <span className="text-sm font-medium text-slate-700">
              {STEP_LABELS[step] || ''}
            </span>
          </div>
          {step < 10 && (
            <button onClick={skipOnboarding}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors min-h-[44px] px-2 flex items-center" data-testid="onboarding-skip">
              Skip for now
            </button>
          )}
        </div>
        {step > 0 && (
          <div className="max-w-lg mx-auto mt-2">
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <motion.div className="h-full bg-[#235347] rounded-full" initial={false}
                animate={{ width: `${progressPct}%` }} transition={{ duration: 0.3 }} />
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 py-6 pb-28">
        <div className="max-w-lg lg:max-w-6xl mx-auto lg:grid lg:grid-cols-12 lg:gap-14 lg:pt-6">
          {/* Stage 17 — desktop-only left editorial panel.
              Completely hidden on mobile. Gives the huge empty canvas
              at ≥1024px warm explanation copy so onboarding reads as
              premium, not unfinished. */}
          <aside
            className="hidden lg:block lg:col-span-5 lg:sticky lg:top-24 lg:self-start"
            data-testid="onboarding-left-rail"
          >
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#235347] font-semibold mb-4">
              {leftCopy.eyebrow}
            </p>
            <h2
              className="text-[30px] leading-[1.15] text-[#0E1C2E] mb-5"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            >
              {leftCopy.title}
            </h2>
            <p className="text-[15px] leading-[1.65] text-slate-600">
              {leftCopy.body}
            </p>
            <p className="mt-8 text-[11px] text-slate-400 leading-relaxed">
              Nothing here is shared with anyone. Everything is editable later.
            </p>
          </aside>

          <div className="lg:col-span-7">
          {completeError && (
            <div
              data-testid="onboarding-error"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {completeError}
            </div>
          )}
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div key={step} custom={direction} variants={variants}
              initial="enter" animate="center" exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}>
              {step === 0 && <OnboardingWelcome userName={user?.name} />}
              {step === 1 && <OnboardingConditions data={data} onChange={setData} />}
              {step === 2 && <OnboardingMedications data={data} onChange={setData} />}
              {step === 3 && <OnboardingCurrentState data={data} onChange={setData} />}
              {step === 4 && <OnboardingHistory data={data} onChange={setData} />}
              {step === 5 && <OnboardingMedicalTeam data={data} onChange={setData} />}
              {step === 6 && <OnboardingLifeContext data={data} onChange={setData} />}
              {step === 7 && <OnboardingGoals data={data} onChange={setData} />}
              {step === 8 && <OnboardingDocuments uploadedDocs={uploadedDocs} setUploadedDocs={setUploadedDocs} />}
              {step === 9 && <OnboardingDataAwareness />}
              {step === 10 && <OnboardingSummary data={data} onEditStep={goToStep} />}
            </motion.div>
          </AnimatePresence>
          </div>{/* /right column */}
        </div>
      </div>

      {/* Footer — safe area aware */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-slate-100 z-20"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}>
        <div className="px-4 pt-3 pb-3">
          <div className="max-w-lg lg:max-w-6xl mx-auto lg:grid lg:grid-cols-12 lg:gap-14">
            <div className="hidden lg:block lg:col-span-5" />
            <div className="lg:col-span-7">
            {step === 0 ? (
              <button data-testid="onboarding-begin" onClick={goForward}
                className="w-full bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors">
                Let's begin
              </button>
            ) : step === 10 ? (
              <button data-testid="onboarding-confirm" onClick={handleComplete} disabled={submitting}
                className="w-full bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? (
                  <><Spinner size={18} className="animate-spin" /> Setting up your companion...</>
                ) : 'Confirm & Start'}
              </button>
            ) : step === 9 ? (
              <button data-testid="onboarding-to-summary" onClick={goForward}
                className="w-full bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors">
                Continue to review
              </button>
            ) : step === 8 ? (
              <div className="flex gap-3">
                <button data-testid="onboarding-skip-docs" onClick={goForward}
                  className="flex-1 py-3.5 text-sm text-slate-500 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors">
                  Skip this step
                </button>
                <button data-testid="onboarding-continue-docs" onClick={goForward}
                  className="flex-[2] bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors">
                  {uploadedDocs.length > 0 ? 'Continue' : 'Skip this step'}
                </button>
              </div>
            ) : step === 5 ? (
              <div className="flex gap-3">
                <button data-testid="onboarding-skip-step" onClick={goForward}
                  className="flex-1 py-3.5 text-sm text-slate-500 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors">
                  Skip this step
                </button>
                <button data-testid="onboarding-continue" onClick={goForward}
                  className="flex-[2] bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors">
                  {(data.medical_team || []).length > 0 ? 'Continue' : 'Skip this step'}
                </button>
              </div>
            ) : step === 2 ? (
              /* Sprint Stage-1 — Onboarding compression.
                 After Conditions + Medications the user has enough for Kindra to be
                 useful. Default exit is "Start using Kindra" (express). Deeper details
                 are an opt-in below. */
              <div className="space-y-3">
                <button
                  data-testid="onboarding-express-finish"
                  onClick={handleComplete}
                  disabled={submitting}
                  className="w-full bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><Spinner size={18} className="animate-spin" /> Setting things up...</>
                  ) : 'Start using Kindra'}
                </button>
                <button
                  data-testid="onboarding-express-add-more"
                  onClick={goForward}
                  disabled={submitting}
                  className="w-full text-sm text-slate-500 hover:text-[#235347] transition-colors py-2"
                >
                  Add more details (optional · ~3 min)
                </button>
                <p className="text-[11px] text-slate-400 text-center leading-relaxed px-4 max-w-xs mx-auto">
                  Kindra will learn the rest from your reports and chats.
                </p>
              </div>
            ) : step === 7 ? (
              <button data-testid="onboarding-continue" onClick={goForward} disabled={!canContinue()}
                className="w-full bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-40">
                Continue
              </button>
            ) : (
              <div className="flex gap-3">
                <button data-testid="onboarding-skip-step" onClick={goForward}
                  className="flex-1 py-3.5 text-sm text-slate-500 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors">
                  Skip this step
                </button>
                <button data-testid="onboarding-continue" onClick={goForward} disabled={!canContinue()}
                  className="flex-[2] bg-[#235347] text-white rounded-full py-3.5 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-40">
                  Continue
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
